import { getOnboardingSessionOptions } from '@labflow/api-client';
import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import { serverFeaturesEnabled } from '../../lib/server-features';
import { requireSession } from '../auth/require-session';
import {
  CONSENT_ROUTE,
  FALLBACK_ROUTE,
  isOnboardingRoute,
  type OnboardingRoute,
} from './require-onboarding';

/**
 * 온보딩 하위 화면 자신의 가드 — D86.
 *
 * `requireOnboarding`은 My Lab 같은 제품 영역을 지키지만, 온보딩 화면들 사이의 이동은 이 함수가
 * 지킨다. 서버가 계산한 현재 단계(`next_route`)와 다른 화면에 직접 URL로 들어오면 그 화면으로
 * 돌려보낸다 — 필수 버전이 `profile_only`인데 `/onboarding/upload`에 들어오거나, `full`인데
 * `/onboarding/profile`에 들어오는 경우가 여기서 걸린다. 이미 완료했으면 My Lab으로 보낸다.
 */
export async function requireOnboardingStage({
  queryClient,
  location,
  path,
}: {
  queryClient: QueryClient;
  location: { href: string };
  path: OnboardingRoute;
}): Promise<void> {
  if (!serverFeaturesEnabled) return;

  const me = await requireSession({ queryClient, location });

  // 동의가 온보딩보다 앞이다 — 완료 판정보다 먼저 본다 (`AC-PF-01-16`)
  if (me.consent_required) throw redirect({ to: CONSENT_ROUTE, replace: true });

  if (me.onboarding_completed_at) {
    throw redirect({ to: '/mylab', replace: true });
  }

  const session = await queryClient.ensureQueryData(getOnboardingSessionOptions());
  const nextRoute = isOnboardingRoute(session.next_route) ? session.next_route : FALLBACK_ROUTE;
  const startsAnalysisFromUpload =
    path === '/onboarding/analyzing' &&
    nextRoute === '/onboarding/upload' &&
    (Boolean(session.job_id) || session.counts.total > 0);
  if (nextRoute !== path && !startsAnalysisFromUpload) {
    throw redirect({ to: nextRoute, replace: true });
  }
}
